/** Origin feats (SRD 5.2, CC-BY-4.0). Every 2024 background grants one. */

export interface FeatDef {
  id: string
  name: string
  /** One line for the card. */
  tagline: string
  /** Rules text, condensed for the sheet's Feats box. */
  text: string
  /** Feats the wizard must ask a follow-up question about. */
  choice?: {
    id: string
    label: string
    help: string
    options: { value: string; label: string; help: string }[]
  }
}

export const ORIGIN_FEATS: FeatDef[] = [
  {
    id: 'alert',
    name: 'Alert',
    tagline: 'You act first, almost always.',
    text: 'Add your Proficiency Bonus to Initiative rolls. When you roll Initiative you can swap your result with a willing ally\'s.',
  },
  {
    id: 'crafter',
    name: 'Crafter',
    tagline: 'You make and mend things, and you buy cheap.',
    text: 'Gain proficiency with three Artisan\'s Tools of your choice. Purchased nonmagical gear costs 20% less, and you craft faster during rests.',
  },
  {
    id: 'healer',
    name: 'Healer',
    tagline: 'You patch people up mid-fight.',
    text: 'As a Utility action, use a Healer\'s Kit to restore 1d6 + 4 hit points plus a die equal to the target\'s largest Hit Die. Once per creature per rest. When you roll a 1 on a healing die, reroll it.',
  },
  {
    id: 'lucky',
    name: 'Lucky',
    tagline: 'Reroll the dice that matter.',
    text: 'You have Luck Points equal to your Proficiency Bonus, regained on a Long Rest. Spend one to give yourself Advantage on a d20 Test, or to impose Disadvantage on an attack against you.',
  },
  {
    id: 'magic-initiate-cleric',
    name: 'Magic Initiate (Cleric)',
    tagline: 'Two divine cantrips and a spell.',
    text: 'Learn two Cleric cantrips and one 1st-level Cleric spell. You can cast the spell once per Long Rest without a slot, or with slots if you have them. Spellcasting ability: Wisdom.',
  },
  {
    id: 'magic-initiate-druid',
    name: 'Magic Initiate (Druid)',
    tagline: 'Two primal cantrips and a spell.',
    text: 'Learn two Druid cantrips and one 1st-level Druid spell. You can cast the spell once per Long Rest without a slot, or with slots if you have them. Spellcasting ability: Wisdom.',
  },
  {
    id: 'magic-initiate-wizard',
    name: 'Magic Initiate (Wizard)',
    tagline: 'Two arcane cantrips and a spell.',
    text: 'Learn two Wizard cantrips and one 1st-level Wizard spell. You can cast the spell once per Long Rest without a slot, or with slots if you have them. Spellcasting ability: Intelligence.',
  },
  {
    id: 'musician',
    name: 'Musician',
    tagline: 'You play, and the party fights better for it.',
    text: 'Gain proficiency with three Musical Instruments. After a rest, you can grant Heroic Inspiration to allies equal to your Proficiency Bonus.',
  },
  {
    id: 'savage-attacker',
    name: 'Savage Attacker',
    tagline: 'Your big hits hit bigger.',
    text: 'Once per turn when you hit with a weapon, you can roll its damage dice twice and use either total.',
  },
  {
    id: 'skilled',
    name: 'Skilled',
    tagline: 'Good at three more things.',
    text: 'Gain proficiency in any combination of three skills or tools of your choice.',
  },
  {
    id: 'tavern-brawler',
    name: 'Tavern Brawler',
    tagline: 'Dangerous with fists, bottles, and furniture.',
    text: 'Your Unarmed Strike deals 1d4 damage. Once per turn, reroll a damage die that lands on 1. You can push a creature 5 feet as part of an Unarmed Strike, once per turn. You are proficient with improvised weapons.',
  },
  {
    id: 'tough',
    name: 'Tough',
    tagline: 'More hit points, every level.',
    text: 'Your hit point maximum increases by twice your character level, and by 2 more each time you level up.',
  },
]

export const FEATS_BY_ID = new Map(ORIGIN_FEATS.map((f) => [f.id, f]))
